import { useState, useEffect, useCallback } from 'react'
import {
  Activity,
  Server,
  Database,
  HardDrive,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import { adminService } from '../../services/adminService'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ServiceStatus {
  name: string
  status: 'healthy' | 'unhealthy' | 'degraded'
  message?: string
  lastCheckedAt?: string
}

interface RedisMetrics {
  connectionCount: number
  memoryUsage: number
  hitRate: number
}

interface DatabaseMetrics {
  activeConnections: number
  idleConnections: number
  waitingConnections: number
}

interface ApplicationMetrics {
  uptime: number
  requestsPerSecond: number
  avgResponseTime: number
}

interface HealthMetrics {
  redis: RedisMetrics
  database: DatabaseMetrics
  application: ApplicationMetrics
}

interface ErrorRateEntry {
  statusCode: number
  endpoint: string
  count: number
}

interface ServiceStatusResponse {
  services: ServiceStatus[]
  hasAlert: boolean
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const REFRESH_INTERVAL = 30_000 // 30 seconds

// ─── Component ─────────────────────────────────────────────────────────────────

export function AdminHealth() {
  const [healthMetrics, setHealthMetrics] = useState<HealthMetrics | null>(null)
  const [errorRates, setErrorRates] = useState<ErrorRateEntry[]>([])
  const [serviceStatus, setServiceStatus] = useState<ServiceStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const fetchAllData = useCallback(async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) setRefreshing(true)
      setError(null)

      const [metricsRes, errorsRes, statusRes] = await Promise.all([
        adminService.getHealthMetrics(),
        adminService.getErrorRates(),
        adminService.getServiceStatus(),
      ])

      const metricsData = metricsRes.data?.metrics || metricsRes.data || null
      setHealthMetrics(metricsData)

      const errorsData = errorsRes.data?.errors || errorsRes.data || []
      setErrorRates(Array.isArray(errorsData) ? errorsData : [])

      const statusData = statusRes.data || { services: [], hasAlert: false }
      setServiceStatus({
        services: statusData.services || [],
        hasAlert: statusData.hasAlert ?? false,
      })

      setLastRefresh(new Date())
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load health metrics')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    fetchAllData()
    const interval = setInterval(() => fetchAllData(), REFRESH_INTERVAL)
    return () => clearInterval(interval)
  }, [fetchAllData])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading health metrics...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
        <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700 dark:text-red-400 font-medium">{error}</p>
        <button
          onClick={() => { setLoading(true); fetchAllData() }}
          className="mt-3 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Platform Health
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Monitor service status, infrastructure metrics, and error rates.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => fetchAllData(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Alert Banner */}
      {serviceStatus?.hasAlert && (
        <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800 dark:text-red-300">
              System Alert: One or more services require attention
            </p>
            <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
              Check the service status cards below for details.
            </p>
          </div>
        </div>
      )}

      {/* Service Status Cards */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Service Status
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {serviceStatus && serviceStatus.services.length > 0 ? (
            serviceStatus.services.map((service) => (
              <ServiceCard key={service.name} service={service} />
            ))
          ) : (
            <div className="col-span-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-center text-sm text-gray-500 dark:text-gray-400">
              No service status data available.
            </div>
          )}
        </div>
      </section>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Redis Metrics */}
        <MetricsCard
          title="Redis Cache"
          icon={HardDrive}
          metrics={
            healthMetrics?.redis
              ? [
                  { label: 'Connections', value: healthMetrics.redis.connectionCount.toString() },
                  { label: 'Memory Usage', value: formatBytes(healthMetrics.redis.memoryUsage) },
                  { label: 'Hit Rate', value: `${(healthMetrics.redis.hitRate * 100).toFixed(1)}%` },
                ]
              : []
          }
        />

        {/* Database Pool Metrics */}
        <MetricsCard
          title="Database Pool"
          icon={Database}
          metrics={
            healthMetrics?.database
              ? [
                  { label: 'Active', value: healthMetrics.database.activeConnections.toString() },
                  { label: 'Idle', value: healthMetrics.database.idleConnections.toString() },
                  { label: 'Waiting', value: healthMetrics.database.waitingConnections.toString() },
                ]
              : []
          }
        />

        {/* Application Metrics */}
        <MetricsCard
          title="Application"
          icon={Activity}
          metrics={
            healthMetrics?.application
              ? [
                  { label: 'Uptime', value: formatUptime(healthMetrics.application.uptime) },
                  { label: 'Req/s', value: healthMetrics.application.requestsPerSecond.toFixed(1) },
                  { label: 'Avg Response', value: `${healthMetrics.application.avgResponseTime.toFixed(0)}ms` },
                ]
              : []
          }
        />
      </div>

      {/* Error Rates */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Error Rates (Past 24h)
        </h2>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          {errorRates.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              <CheckCircle className="h-6 w-6 text-green-500 mx-auto mb-2" />
              No errors recorded in the past 24 hours.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                      Status Code
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                      Endpoint
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-gray-700 dark:text-gray-300">
                      Count
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {errorRates.map((entry, idx) => (
                    <tr key={`${entry.statusCode}-${entry.endpoint}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3">
                        <StatusCodeBadge code={entry.statusCode} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-400">
                        {entry.endpoint}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                        {entry.count.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Auto-refresh indicator */}
      <div className="flex items-center justify-center gap-2 py-2">
        <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Auto-refreshing every 30 seconds
        </span>
      </div>
    </div>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function ServiceCard({ service }: { service: ServiceStatus }) {
  const statusConfig = {
    healthy: {
      icon: CheckCircle,
      color: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      dot: 'bg-green-500',
    },
    unhealthy: {
      icon: XCircle,
      color: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      dot: 'bg-red-500',
    },
    degraded: {
      icon: AlertTriangle,
      color: 'text-yellow-600 dark:text-yellow-400',
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      dot: 'bg-yellow-500',
    },
  }

  const config = statusConfig[service.status] || statusConfig.healthy
  const Icon = config.icon

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} p-4`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Server className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {service.name}
          </span>
        </div>
        <Icon className={`h-5 w-5 ${config.color}`} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${config.dot}`} />
        <span className={`text-xs font-medium capitalize ${config.color}`}>
          {service.status}
        </span>
      </div>
      {service.message && (
        <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
          {service.message}
        </p>
      )}
    </div>
  )
}

function MetricsCard({
  title,
  icon: Icon,
  metrics,
}: {
  title: string
  icon: React.ElementType
  metrics: { label: string; value: string }[]
}) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      </div>
      {metrics.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No data available</p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {metrics.map((metric) => (
            <div key={metric.label} className="text-center">
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {metric.value}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {metric.label}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function StatusCodeBadge({ code }: { code: number }) {
  let colorClasses = 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  if (code >= 500) {
    colorClasses = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
  } else if (code >= 400) {
    colorClasses = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
  }

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClasses}`}>
      {code}
    </span>
  )
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const value = bytes / Math.pow(1024, i)
  return `${value.toFixed(1)} ${units[i] || 'TB'}`
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`
}
